export interface ConversationShortcut {
  url: string;
  label: string;
}

export interface Figure {
  artifact_id: string;
  artifact_name?: string;
  content: {
    data: any[];
    layout: any;
  };
  summary?: string;
  code?: string;
}

// DataFrame data in pandas "split" orient format - preserves row order
export interface DataframeSplitData {
  columns: string[];
  index: (string | number)[];
  data: any[][];
}

export interface Dataframe {
  artifact_id: string;
  artifact_name?: string;
  content: {
    data: DataframeSplitData;
    displayedRows: number;
    totalRows: number;
    totalColumns?: number;
  };
  summary?: string;
  code?: string;
}

export interface DataValue {
  artifact_id: string;
  content: string;
  summary?: string;
  code?: string;
}

export interface TextFile {
  artifact_id: string;
  content: {
    file_name: string | null;
    index_artifact_id?: string | null;
  };
  summary?: string;
  code?: string;
}

export interface BlobFile {
  artifact_id: string;
  content: {
    file_name: string | null;
    index_artifact_id?: string | null;
  };
  summary?: string;
  code?: string;
}

export interface ImageArtifact {
  artifact_id: string;
  artifact_name?: string;
  /** Canonical filename for this artifact (e.g. "chart.png"). Preferred for alt text and download attribute. */
  filename?: string;
  content: {
    /** Signed download URL for the image file. */
    url: string;
    /** MIME type (e.g. "image/png"). */
    media_type?: string;
  };
  summary?: string;
  code?: string;
}

// Filter configuration from cohort explorer filter builder (loosely typed - validated on backend)
export interface FilterConfiguration {
  criteria: unknown[];
}

export interface ToolCallArgs {
  instructions?: string;
  raw?: string;
  [key: string]: any;
}

export interface ToolCallContent {
  raw?: string;
  [key: string]: any;
}

export interface ToolCall {
  tool_call_id: string;
  tool: string;
  toolName: string;
  args: ToolCallArgs;
  content: ToolCallContent;
  timestamp: string;
  running?: boolean;
  cancelled?: boolean;
}

export interface MessagePart {
  type: "content" | "toolCall" | "cancelled" | "thinking" | "filters";
  content?: string;
  toolCall?: ToolCall;
  filters?: FilterConfiguration;
  order: number;
}

// String literal union types are appropriate (not objects)
export type MessageKind =
  | "user_chat"
  | "model_chat"
  | "tool_call"
  | "tool_return"
  | "heartbeat"
  | "cancelled";

// Feedback type enum matching backend FeedbackType enum
export type FeedbackType = "BINARY" | "FIVE_POINT" | "TEXT";

// Request schema for creating feedback
export interface FeedbackCreate {
  feedback_type: FeedbackType;
  feedback_value: number | null; // 0/1 for BINARY, 1-5 for FIVE_POINT, null for TEXT
  text?: string | null;
  impact?: number | null; // 1-4 scale, optional
}

// Response schema for turn-level feedback
export interface TurnFeedback extends FeedbackCreate {
  turn_id: string;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  content: string;
  kind: MessageKind;
  timestamp: Date;
  rawJson?: string;
  error?: string;
  warning?: string;
  parts?: MessagePart[];
  // Indicates the agent message is actively streaming and should show inline progress
  streaming?: boolean;
  // Indicates the message generation was cancelled by the user
  cancelled?: boolean;

  // AWS XRay trace ID for debugging
  traceId?: string;

  // Indicates the message has been persisted to database and is ready for feedback
  // Set to true when message_persisted event is received from backend
  feedbackReady?: boolean;

  turnFeedback?: TurnFeedback | null;
}

export interface McpGuidelines {
  accepted: boolean | null;
  isAccepting: boolean;
  onAccept: () => void;
}

export interface MessageGroup {
  id: string;
  user: Message;
  agent: Message[];
}

export type ReviewStatus =
  | "UNREVIEWED"
  | "REVIEWING"
  | "TICKETED"
  | "WONT_FIX"
  | "RESOLVED"
  | "NO_ACTION";

export const ConversationStatus = {
  READY: "READY",
  MESSAGE_IN_PROGRESS: "MESSAGE_IN_PROGRESS",
  MESSAGE_CANCELLED: "MESSAGE_CANCELLED",
  INACTIVE: "INACTIVE",
} as const;
export type ConversationStatus = (typeof ConversationStatus)[keyof typeof ConversationStatus];

export interface RunStatusResponse {
  is_running: boolean;
}

export interface Conversation {
  id: string;
  user_id: string;
  app_id: string;
  project_id: string;
  agent_id?: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
  artifact_count: number;
  display_name?: string;
  status?: ConversationStatus;
  review_status?: ReviewStatus;
  review_status_updated_at?: string;
  owner_given_name?: string | null;
  owner_family_name?: string | null;
  owner_image_url?: string | null;
  dataset_name?: string | null;
}

/**
 * Custom metadata attached to assistant ThreadMessages via `metadata.custom`.
 * Written during history conversion; read by
 * AssistantMessage (cancelled flag) and useUserFeedbackInfo (feedback).
 */
export interface AssistantMessageCustomMetadata extends Record<string, unknown> {
  /** Turn ID (trace_id) for this turn — used for turn-level feedback. */
  turnId?: string;
  /** Existing turn-level feedback (from history). */
  feedback?: TurnFeedback;
  /** True when the agent run was cancelled. */
  cancelled?: boolean;
}

export interface ArtifactNode {
  id: string;
  label: string;
  type: string;
  source_table: string | null;
  created_by: "data_retriever" | "python_analyzer" | null;
}

export interface ArtifactEdge {
  from_id: string;
  to_id: string;
}

export interface SourceTableNode {
  id: string;
  table_name: string;
}

export interface CodeNode {
  id: string;
  artifact_id: string;
  parents: string[];
}

export interface ArtifactLineageGraph {
  nodes: ArtifactNode[];
  source_table_nodes: SourceTableNode[];
  code_nodes: CodeNode[];
  edges: ArtifactEdge[];
  mermaid: string;
}

export interface PartialSkill {
  id: string;
  name: string;
  description?: string;
  tags?: string[];
  scope: "SYSTEM" | "DATASET" | "USER";
  scope_id: string | null;
  enabled: boolean;
  source_template_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Skill extends PartialSkill {
  content: string;
}

export interface SkillCreate {
  name: string;
  description?: string;
  content: string;
  tags?: string[];
  scope?: "SYSTEM" | "DATASET" | "USER";
  scope_id?: string;
  source_template_id?: string | null;
}

export interface SkillUpdate {
  name?: string;
  description?: string;
  content?: string;
  tags?: string[];
  enabled?: boolean;
}

export interface SkillsListResponse {
  system: PartialSkill[];
  user: PartialSkill[];
  dataset: PartialSkill[];
}
